import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';
import {
  PageHeader, Card, Btn, Alert, DataTable, StandardModal,
  FilterPills, KPIGrid,
  colors, spacing, typography, radius,
  inputStyle, selectStyle,
} from '../design-system';

const EVENT_LABELS = {
  'accreditation.granted':     'Accreditation Granted',
  'accreditation.suspended':   'Accreditation Suspended',
  'accreditation.revoked':     'Accreditation Revoked',
  'certificate.issued':        'Certificate Issued',
  'certificate.expiring_soon': 'Certificate Expiring Soon',
  'certificate.expired':       'Certificate Expired',
  'capa.created':              'CAPA Created',
  'capa.closed':               'CAPA Closed',
  'audit.scheduled':           'Audit Scheduled',
  'audit.completed':           'Audit Completed',
  'application.submitted':     'Application Submitted',
  'application.decision':      'Application Decision',
};

const STATUS_COLORS = {
  active: '#10b981',
  inactive: '#94a3b8',
  error: '#ef4444',
};

function DeliveryStatusDot({ status }) {
  const color = status === 'success' ? '#10b981' : status === 'failed' ? '#ef4444' : '#f59e0b';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: typography.fontSizes.xs }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block' }} />
      <span style={{ color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{status}</span>
    </span>
  );
}

function DeliveryLog({ subId, onClose }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosClient.get(`/webhooks/${subId}/deliveries`)
      .then(r => setDeliveries(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subId]);

  const columns = [
    {
      key: 'delivered_at',
      label: 'When',
      render: v => v ? (
        <span style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
          {new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
      ) : '—',
    },
    { key: 'event_type', label: 'Event', render: v => EVENT_LABELS[v] || v },
    { key: 'status', label: 'Status', render: v => <DeliveryStatusDot status={v} /> },
    {
      key: 'response_status',
      label: 'HTTP',
      render: v => v ? (
        <span style={{
          fontFamily: 'monospace',
          fontSize: typography.fontSizes.xs,
          color: v >= 200 && v < 300 ? '#10b981' : '#ef4444',
        }}>
          {v}
        </span>
      ) : '—',
    },
    {
      key: 'error_message',
      label: 'Error',
      render: v => v ? (
        <span style={{ fontSize: typography.fontSizes.xs, color: '#ef4444', fontFamily: 'monospace' }}>
          {v.length > 60 ? v.slice(0, 60) + '…' : v}
        </span>
      ) : <span style={{ color: colors.textMuted }}>—</span>,
    },
  ];

  return (
    <StandardModal title="Delivery Log" onClose={onClose} width={780}>
      <DataTable
        columns={columns}
        rows={deliveries}
        loading={loading}
        keyField="id"
        emptyMessage="No deliveries recorded yet."
      />
    </StandardModal>
  );
}

function SubscriptionModal({ sub, onClose, onSaved, allEvents }) {
  const editing = !!sub;
  const [form, setForm] = useState({
    url: sub?.url || '',
    events: sub?.events || [],
    active: sub?.active ?? true,
    description: sub?.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleEvent = (evt) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(evt)
        ? f.events.filter(e => e !== evt)
        : [...f.events, evt],
    }));
  };

  const selectAll = () => setForm(f => ({ ...f, events: allEvents }));
  const clearAll = () => setForm(f => ({ ...f, events: [] }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.events.length === 0) { setError('Select at least one event.'); return; }
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await axiosClient.patch(`/webhooks/${sub.id}`, form);
      } else {
        await axiosClient.post('/webhooks/', form);
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed.');
      setSaving(false);
    }
  };

  const FieldLabel = ({ children }) => (
    <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 6 }}>
      {children}
    </div>
  );

  return (
    <StandardModal
      title={editing ? 'Edit Webhook' : 'New Webhook Subscription'}
      onClose={onClose}
      width={560}
      footer={
        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" size="md" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" size="md" type="submit" form="webhook-form" loading={saving}>
            {editing ? 'Save Changes' : 'Create Subscription'}
          </Btn>
        </div>
      }
    >
      {error && <Alert type="error" message={error} />}
      <form id="webhook-form" onSubmit={submit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <div>
            <FieldLabel>Endpoint URL</FieldLabel>
            <input
              style={inputStyle}
              type="url"
              value={form.url}
              onChange={e => set('url', e.target.value)}
              placeholder="https://your-service.example.com/webhooks"
              required
            />
          </div>
          <div>
            <FieldLabel>Description (optional)</FieldLabel>
            <input
              style={inputStyle}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="e.g. Compliance dashboard integration"
            />
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <FieldLabel>Events to Subscribe</FieldLabel>
              <div style={{ display: 'flex', gap: spacing.sm }}>
                <button type="button" onClick={selectAll} style={{ fontSize: typography.fontSizes.xs, color: colors.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  Select all
                </button>
                <span style={{ color: colors.border }}>·</span>
                <button type="button" onClick={clearAll} style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  Clear
                </button>
              </div>
            </div>
            <div style={{
              border: `1px solid ${colors.border}`,
              borderRadius: radius.md,
              overflow: 'hidden',
              maxHeight: 260,
              overflowY: 'auto',
            }}>
              {allEvents.map((evt, i) => (
                <label
                  key={evt}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.sm,
                    padding: `10px ${spacing.md}`,
                    cursor: 'pointer',
                    borderBottom: i < allEvents.length - 1 ? `1px solid ${colors.border}` : 'none',
                    background: form.events.includes(evt) ? '#eff6ff' : 'transparent',
                    transition: 'background 100ms',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.events.includes(evt)}
                    onChange={() => toggleEvent(evt)}
                    style={{ accentColor: colors.primary }}
                  />
                  <div>
                    <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textPrimary }}>
                      {EVENT_LABELS[evt] || evt}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '11px', color: colors.textMuted }}>{evt}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {editing && (
            <label style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, cursor: 'pointer', fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>
              <input
                type="checkbox"
                checked={form.active}
                onChange={e => set('active', e.target.checked)}
                style={{ accentColor: colors.primary }}
              />
              Active (receive deliveries)
            </label>
          )}
        </div>
      </form>
    </StandardModal>
  );
}

function SecretModal({ sub, onClose }) {
  const [secret, setSecret] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);

  const rotate = async () => {
    setRotating(true);
    try {
      const res = await axiosClient.post(`/webhooks/${sub.id}/rotate-secret`);
      setSecret(res.data.secret);
    } catch {
      // ignore
    } finally {
      setRotating(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <StandardModal
      title="Signing Secret"
      onClose={onClose}
      width={500}
      footer={<Btn variant="ghost" size="md" onClick={onClose}>Close</Btn>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <div style={{
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: radius.md,
          padding: spacing.md,
          fontSize: typography.fontSizes.sm,
          color: '#92400e',
          lineHeight: 1.5,
        }}>
          Payloads are signed with HMAC-SHA256. Verify the <code style={{ fontFamily: 'monospace' }}>X-BOSS-Signature</code> header in your endpoint to confirm authenticity.
        </div>

        {secret ? (
          <div>
            <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 6 }}>
              New Secret (copy now — shown once)
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              background: '#f8fafc',
              border: `1px solid ${colors.border}`,
              borderRadius: radius.md,
              padding: spacing.sm,
            }}>
              <code style={{ flex: 1, fontFamily: 'monospace', fontSize: typography.fontSizes.xs, wordBreak: 'break-all', color: colors.textPrimary }}>
                {secret}
              </code>
              <Btn variant="secondary" size="sm" onClick={copy}>
                {copied ? 'Copied' : 'Copy'}
              </Btn>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ margin: 0, fontSize: typography.fontSizes.sm, color: colors.textSecondary, lineHeight: 1.6 }}>
              Rotating the secret immediately invalidates the previous one. All future deliveries will use the new secret.
            </p>
            <div style={{ marginTop: spacing.md }}>
              <Btn variant="danger" size="md" onClick={rotate} loading={rotating}>
                Rotate Secret
              </Btn>
            </div>
          </div>
        )}
      </div>
    </StandardModal>
  );
}

export default function WebhookManagement() {
  const [subs, setSubs] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [secretModal, setSecretModal] = useState(null);
  const [deliveryLog, setDeliveryLog] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [msg, setMsg] = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, eventsRes] = await Promise.all([
        axiosClient.get('/webhooks/'),
        axiosClient.get('/webhooks/events'),
      ]);
      setSubs(subsRes.data || []);
      setAllEvents(eventsRes.data || []);
    } catch {
      flash('Could not load webhooks.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async (id) => {
    try {
      await axiosClient.post(`/webhooks/${id}/test`);
      flash('Test payload sent.');
    } catch (err) {
      flash(err?.response?.data?.detail || 'Test failed.', 'error');
    }
  };

  const confirmDelete = async () => {
    try {
      await axiosClient.delete(`/webhooks/${deleteTarget.id}`);
      setDeleteTarget(null);
      load();
      flash('Subscription deleted.');
    } catch {
      flash('Delete failed.', 'error');
    }
  };

  const filtered = subs.filter(s => {
    if (!statusFilter) return true;
    if (statusFilter === 'active') return s.active;
    if (statusFilter === 'inactive') return !s.active;
    return true;
  });

  const kpis = [
    { label: 'Total Subscriptions', value: subs.length, color: colors.primary },
    { label: 'Active', value: subs.filter(s => s.active).length, color: '#10b981' },
    { label: 'Inactive', value: subs.filter(s => !s.active).length, color: '#94a3b8' },
    {
      label: 'Event Types Covered',
      value: new Set(subs.flatMap(s => s.events || [])).size,
      color: '#8b5cf6',
    },
  ];

  const STATUS_FILTER_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  const columns = [
    {
      key: 'url',
      label: 'Endpoint',
      render: (v, row) => (
        <div>
          <div style={{
            fontFamily: 'monospace',
            fontSize: typography.fontSizes.sm,
            color: colors.textPrimary,
            maxWidth: 300,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {v}
          </div>
          {row.description && (
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 2 }}>
              {row.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'events',
      label: 'Events',
      render: (v) => {
        const evts = v || [];
        const shown = evts.slice(0, 2);
        const more = evts.length - 2;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {shown.map(e => (
              <span key={e} style={{
                padding: '2px 7px',
                borderRadius: radius.sm,
                background: '#eff6ff',
                color: '#1e40af',
                fontSize: '11px',
                fontWeight: 600,
                fontFamily: 'monospace',
              }}>
                {e}
              </span>
            ))}
            {more > 0 && (
              <span style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, alignSelf: 'center' }}>
                +{more} more
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'failure_count',
      label: 'Failures',
      render: (v) => (
        <span style={{
          fontSize: typography.fontSizes.sm,
          fontWeight: typography.fontWeights.semibold,
          color: v > 0 ? '#ef4444' : colors.textMuted,
        }}>
          {v || 0}
        </span>
      ),
    },
    {
      key: 'active',
      label: 'Status',
      render: (v) => (
        <span style={{
          padding: '3px 10px',
          borderRadius: radius.pill,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          background: v ? '#dcfce7' : '#f1f5f9',
          color: v ? '#166534' : '#64748b',
        }}>
          {v ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'id',
      label: '',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" size="sm" onClick={() => setDeliveryLog(row)}>Log</Btn>
          <Btn variant="ghost" size="sm" onClick={() => sendTest(row.id)}>Test</Btn>
          <Btn variant="ghost" size="sm" onClick={() => setSecretModal(row)}>Secret</Btn>
          <Btn variant="ghost" size="sm" onClick={() => setEditing(row)}>Edit</Btn>
          <Btn variant="danger" size="sm" onClick={() => setDeleteTarget(row)}>Delete</Btn>
        </div>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: spacing.lg }}>
      <PageHeader
        title="Outbound Webhooks"
        subtitle="Subscribe external services to accreditation lifecycle events via HMAC-signed HTTP POST"
        actions={
          <Btn variant="primary" size="md" onClick={() => setShowCreate(true)}>
            New Subscription
          </Btn>
        }
      />

      {msg && <Alert type={msg.type} message={msg.text} />}

      <KPIGrid kpis={kpis} columns={4} />

      {/* How it works */}
      <Card style={{ margin: `${spacing.lg} 0`, padding: spacing.lg }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: spacing.lg }}>
          {[
            ['Event fires', 'An action occurs in ISO BOSS (e.g. accreditation granted)'],
            ['Payload signed', 'A JSON payload is signed with HMAC-SHA256 using your secret'],
            ['POST delivered', 'ISO BOSS POSTs to your endpoint with X-BOSS-Signature header'],
            ['You verify', 'Your service validates the signature and processes the event'],
          ].map(([step, desc], i) => (
            <div key={step} style={{ display: 'flex', gap: spacing.md }}>
              <div style={{
                width: 28, height: 28,
                borderRadius: '50%',
                background: colors.primary,
                color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: typography.fontSizes.sm,
                fontWeight: 700,
                flexShrink: 0,
              }}>
                {i + 1}
              </div>
              <div>
                <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary }}>{step}</div>
                <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, lineHeight: 1.4, marginTop: 2 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg, flexWrap: 'wrap', gap: spacing.sm }}>
          <FilterPills
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
            {filtered.length} subscription{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>
        <DataTable
          columns={columns}
          rows={filtered}
          loading={loading}
          keyField="id"
          emptyMessage="No webhook subscriptions yet. Create one to start receiving events."
        />
      </Card>

      {(showCreate || editing) && (
        <SubscriptionModal
          sub={editing}
          allEvents={allEvents}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => {
            setShowCreate(false);
            setEditing(null);
            load();
            flash(editing ? 'Subscription updated.' : 'Subscription created.');
          }}
        />
      )}

      {secretModal && (
        <SecretModal sub={secretModal} onClose={() => setSecretModal(null)} />
      )}

      {deliveryLog && (
        <DeliveryLog subId={deliveryLog.id} onClose={() => setDeliveryLog(null)} />
      )}

      {deleteTarget && (
        <StandardModal
          title="Delete Subscription"
          onClose={() => setDeleteTarget(null)}
          danger
          footer={
            <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" size="md" onClick={() => setDeleteTarget(null)}>Cancel</Btn>
              <Btn variant="danger" size="md" onClick={confirmDelete}>Delete</Btn>
            </div>
          }
        >
          <p style={{ margin: 0, color: colors.textSecondary, fontSize: typography.fontSizes.sm, lineHeight: 1.6 }}>
            Delete the subscription for <strong>{deleteTarget.url}</strong>? This cannot be undone and will stop all future deliveries to this endpoint.
          </p>
        </StandardModal>
      )}
    </div>
  );
}
